import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SessionStorageService } from '../../../core/services/session-storage/session-storage.service';
import { AD_ACCEPTED_FORMATS, AD_BRANCHES, AD_FILE_KIND_META, AD_REVIEW_CRITERIA, AdAttachment, AdAuditEntry, AdComment, AdCommentPin, AdCommentStatus, AdFileKind, AdReview, AdsListFilter, AdStatus, BranchAdSummary, MARKETING_REVIEWER_ROLES, MarketingAd, MarketingReviewSummary, PENDING_REVIEW_STATUSES, REVIEW_QUEUE_STATUSES, ReviewQueueFile, ReviewQueueRow, SaveAdPayload, } from '../marketing-review-interfaces/marketing-review.interface';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { environment } from '../../../../environments/environment';
import { ReviewAdModel } from '../review-ad/review-ad-models/review-ad.model';

/**
 * Folder holding the marketing material samples supplied by the business
 * (`D:\Marketing_review\Marketing Review Sample`), copied in so the seeded ads carry real files of
 * every accepted type rather than placeholder images.
 *
 * ⚠️ Demo assets only — they ship with the bundle. Drop the folder once the module reads real
 * uploads from the backend.
 */
const SAMPLES: string = 'assets/marketing-review-samples/';

/** File family per extension — the primary signal behind `fileKindOf()`. */
const KIND_BY_EXTENSION: Record<string, AdFileKind> = {
    png: 'image', jpg: 'image', jpeg: 'image', gif: 'image', webp: 'image', bmp: 'image', svg: 'image',
    mp4: 'video', mov: 'video', webm: 'video', m4v: 'video',
    pdf: 'pdf',
    ppt: 'slides', pptx: 'slides',
    doc: 'document', docx: 'document', txt: 'document',
    xls: 'spreadsheet', xlsx: 'spreadsheet', csv: 'spreadsheet',
};

/** Badge text per extension, so `.pptx` reads `PPT` rather than its MIME subtype. */
const BADGE_BY_EXTENSION: Record<string, string> = {
    png: 'PNG', jpg: 'JPG', jpeg: 'JPG', gif: 'GIF', webp: 'WEBP', bmp: 'BMP', svg: 'SVG',
    mp4: 'MP4', mov: 'MOV', webm: 'WEBM', m4v: 'MP4',
    pdf: 'PDF',
    ppt: 'PPT', pptx: 'PPT',
    doc: 'DOC', docx: 'DOC', txt: 'TXT',
    xls: 'XLS', xlsx: 'XLS', csv: 'CSV',
};

/**
 * Marketing Review data access.
 *
 * ⚠️ The backend for this module does not exist yet, so every method resolves from an in-memory
 * store seeded with `MOCK_ADS`. The store lives in a `BehaviorSubject` so a create or a review on
 * one page is reflected on the others for the rest of the session (it resets on reload).
 *
 * Each method carries the axios block it will become, matching the shape used across the app
 * (see `pages/campaign/campaign-service/campaign-service.ts`). Swapping one over means deleting
 * the mock body, uncommenting the request, and adding its endpoint key to all four
 * `src/environments/environment*.ts` files (`''` in `environment.ts`).
 */
@Injectable({
    providedIn: 'root'
})
export class MarketingReviewService {

    private sessionStorageService = inject(SessionStorageService);

    /** Simulated network latency, so the page spinners are actually exercised. */
    private readonly MOCK_LATENCY_MS: number = 300;

    private readonly adsSubject = new BehaviorSubject<MarketingAd[]>(this.buildMockAds());

    /** Live view of the ad store. Subscribe in `ngOnInit` and assign to a plain field. */
    readonly ads$: Observable<MarketingAd[]> = this.adsSubject.asObservable();

    constructor() { }

    // ── Reads ───────────────────────────────────────────────────────────────────

    /** All ads, newest first, optionally narrowed by the list filter. */
    async listAds(filter?: Partial<AdsListFilter>): Promise<MarketingAd[]> {
        await this.simulateLatency();

        // TODO(backend): replace the block below with the real call.
        // const API_URL: string = environment.baseUrl + environment.apis.listMarketingAds;
        // const REQUEST_CONFIG: AxiosRequestConfig = {
        //   method: 'POST',
        //   maxBodyLength: Infinity,
        //   url: API_URL,
        //   headers: {
        //     'Content-Type': 'application/json',
        //     'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
        //   },
        //   data: filter ?? {}
        // };
        // return await axios.request(REQUEST_CONFIG)
        //   .then((httpResponse) => httpResponse.data.responsedata)
        //   .catch((error) => {
        //     if (error.response) {
        //       throw new AxiosError(error.response.data?.message || 'API Error', error.response.status.toString());
        //     } else {
        //       throw new AxiosError('Network Error: Could not reach the server.', '500');
        //     }
        //   });

        return this.applyFilter(this.snapshot(), filter);
    }

    async getAdById(applicationId: number) {
        const API_URL: string = environment.baseUrl + environment.apis.actionbyMarketingReview + `/${applicationId}`;
        const REQUEST_CONFIG: AxiosRequestConfig = {
            method: 'GET',
            maxBodyLength: Infinity,
            url: API_URL,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
            },
        };

        return await axios.request(REQUEST_CONFIG)
            .then((httpResponse) => {
                return JSON.stringify(httpResponse);
            })
            .catch((error) => {
                throw new AxiosError(error.response.data, error.status);
            });
    }

    /**
     * Whether the signed-in user is on the marketing team, which decides the dashboard view.
     * See `MARKETING_REVIEWER_ROLES` — currently a placeholder, so this is `false` for everyone.
     */
    isMarketingReviewer(): boolean {
        return MARKETING_REVIEWER_ROLES.includes(this.sessionStorageService.getCurrentUserRole());
    }

    /**
     * True when the signed-in user raised the ad. Matched on rep code, falling back to the display
     * name for records raised without one.
     */
    private isOwnedByCurrentUser(ad: MarketingAd): boolean {
        const repCode: string = String(this.sessionStorageService.getCurrentRepCode() ?? '');
        // Same fallback the seed uses for `createdBy`, so the dashboard is not empty in a session
        // without stored user details.
        const userName: string = this.sessionStorageService.getCurrentUserPersonName() || 'Current User';

        return repCode ? ad.createdByRepCode === repCode : ad.createdBy === userName;
    }

    /**
     * Branches selectable on the application form.
     *
     * TODO(backend): serve this from `environment.apis.getBranchName`
     * (`/v2/pias/agentmaintenance/getbranch`) with the Authorization header, as Agent Maintenance
     * already does, instead of the `AD_BRANCHES` mock.
     */
    async listBranches(): Promise<string[]> {
        await this.simulateLatency();
        return [...AD_BRANCHES];
    }

    /** KPI counts plus the per-branch rollup for the dashboard, over the user's `dashboardAds()`. */
    async getDashboardSummary(): Promise<MarketingReviewSummary> {
        await this.simulateLatency();

        const ads: MarketingAd[] = this.dashboardAds();
        const branchNames: string[] = this.branchNamesOf(ads);

        // An application raised for several branches counts once in each of their rollups, so the
        // per-branch totals here can add up to more than `totalAds`.
        const branches: BranchAdSummary[] = branchNames.map((branch) => {
            const branchAds: MarketingAd[] = ads.filter((ad) => ad.branches.includes(branch));
            const submittedDates: number[] = branchAds
                .filter((ad) => ad.submittedOn)
                .map((ad) => new Date(ad.submittedOn as string).getTime());

            return {
                branch,
                total: branchAds.length,
                pendingReview: branchAds.filter((ad) => this.isPendingReview(ad.status)).length,
                approved: branchAds.filter((ad) => ad.status === 'Approved').length,
                returned: branchAds.filter((ad) => ad.status === 'Returned').length,
                rejected: branchAds.filter((ad) => ad.status === 'Rejected').length,
                lastSubmittedOn: submittedDates.length
                    ? new Date(Math.max(...submittedDates)).toISOString()
                    : null,
            };
        });

        return {
            totalAds: ads.length,
            pendingReview: ads.filter((ad) => this.isPendingReview(ad.status)).length,
            approved: ads.filter((ad) => ad.status === 'Approved').length,
            returned: ads.filter((ad) => ad.status === 'Returned').length,
            rejected: ads.filter((ad) => ad.status === 'Rejected').length,
            branches,
        };
    }

    /**
     * The dashboard table: one row per ad in the user's `dashboardAds()`, with its attached files
     * resolved for the expanded view.
     */
    async listReviewQueue(): Promise<ReviewQueueRow[]> {
        await this.simulateLatency();

        return this.dashboardAds().map((ad) => {
            const files: ReviewQueueFile[] = ad.attachments.map((attachment) => {
                const kind: AdFileKind = this.fileKindOf(attachment.fileName);
                const format: string = this.formatLabelOf(attachment.fileName);

                return {
                    attachment,
                    kind,
                    format,
                    icon: this.iconOf(attachment.fileName),
                    tone: this.toneOf(attachment.fileName),
                    meta: [format, this.fileSizeLabel(attachment.fileSizeKb)].join(' · '),
                    thumbnailUrl: kind === 'image' ? attachment.url : '',
                };
            });

            return {
                ad,
                initials: this.initialsOf(ad.createdBy),
                formats: [...new Set(files.map((file) => file.format))],
                files,
                daysWaiting: this.daysWaiting(ad),
            };
        });
    }

    /**
     * What the dashboard shows, by role, newest first:
     *
     *   - Marketing team — every user's ads that have been submitted. Other people's drafts are
     *     left out: an unsent draft has not reached the team and there is nothing to review.
     *   - Creator — only the ads they raised, drafts included, so a saved draft is found here.
     *
     * ⚠️ Client-side scoping only: the real endpoint must filter server-side so another adviser's
     * material never reaches a creator's browser.
     */
    private dashboardAds(): MarketingAd[] {
        const ads: MarketingAd[] = this.isMarketingReviewer()
            ? this.snapshot().filter((ad) => REVIEW_QUEUE_STATUSES.includes(ad.status))
            : this.snapshot().filter((ad) => this.isOwnedByCurrentUser(ad));

        return ads.sort((a, b) => this.submittedTime(b) - this.submittedTime(a));
    }

    private submittedTime(ad: MarketingAd): number {
        return new Date(ad.submittedOn ?? ad.createdOn).getTime();
    }

    /** Days an ad has been waiting since submission; `0` when it was never submitted. */
    daysWaiting(ad: MarketingAd): number {
        if (!ad.submittedOn) {
            return 0;
        }
        const elapsedMs: number = Date.now() - new Date(ad.submittedOn).getTime();
        return Math.max(0, Math.floor(elapsedMs / (1000 * 60 * 60 * 24)));
    }

    /**
     * Immutable-looking activity trail for one ad, assembled from the fields the store holds:
     * creation, submission, each comment, then one entry per recorded review.
     *
     * ⚠️ The design's audit log also lists media revisions, which are not modelled — nothing is
     * invented here, so the trail shows only what actually happened.
     */
    buildAuditTrail(ad: MarketingAd): AdAuditEntry[] {
        const entries: AdAuditEntry[] = [
            {
                at: ad.createdOn,
                action: 'Request created',
                detail: `${ad.channel} · ${ad.category}`,
                actor: `${ad.createdBy}${ad.createdByRepCode ? ` (${ad.createdByRepCode})` : ''}`,
                tone: 'info',
            },
        ];

        if (ad.submittedOn) {
            const fileCount: number = ad.attachments.length;
            entries.push({
                at: ad.submittedOn,
                action: 'Submitted for review',
                detail: fileCount
                    ? `${fileCount} media file${fileCount === 1 ? '' : 's'} attached`
                    : 'No media attached',
                actor: ad.createdBy,
                tone: 'submitted',
            });
        }

        ad.comments.forEach((comment) => {
            const pinned: string = comment.pin ? ` #${comment.pinNo} on ${comment.pin.pageLabel}` : '';
            entries.push({
                at: comment.editedOn ?? comment.postedOn,
                action: `${comment.pin ? 'Pinned comment' : 'Comment'}${pinned} ${comment.editedOn ? 'edited' : 'added'}`,
                detail: comment.text,
                actor: `${comment.authorName} (${comment.authorRole})`,
                tone: 'info',
            });

            if (comment.status === 'Resolved' && comment.resolvedOn) {
                entries.push({
                    at: comment.resolvedOn,
                    action: `Pinned comment #${comment.pinNo} resolved`,
                    detail: comment.text,
                    actor: comment.resolvedBy ?? '',
                    tone: 'info',
                });
            }
        });

        [...ad.reviews]
            .sort((a, b) => new Date(a.reviewedOn).getTime() - new Date(b.reviewedOn).getTime())
            .forEach((review) => {
                const failed: number = review.criteria.filter((criterion) => criterion.response === 'No').length;

                // The review page records a decision with comments only, so `criteria` is empty on
                // anything raised there. Only describe criteria when the review actually has some.
                let detail: string;
                if (failed) {
                    detail = `${failed} criteri${failed === 1 ? 'on' : 'a'} marked No`;
                } else if (review.criteria.length) {
                    detail = 'All criteria cleared';
                } else {
                    detail = 'Decision recorded with comments';
                }

                entries.push({
                    at: review.reviewedOn,
                    action: `Status changed to ${review.decision}`,
                    detail,
                    actor: `${review.reviewerName} (${review.reviewerRole})`,
                    tone: review.decision.toLowerCase() as AdAuditEntry['tone'],
                });
            });

        return entries.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
    }

    /** Two-letter avatar initials, e.g. `Galvin Lim` → `GL`. */
    private initialsOf(name: string): string {
        const parts: string[] = name.trim().split(/\s+/).filter(Boolean);
        if (!parts.length) {
            return '–';
        }
        const first: string = parts[0][0] ?? '';
        const last: string = parts.length > 1 ? parts[parts.length - 1][0] ?? '' : '';
        return (first + last).toUpperCase();
    }

    // ── Writes ──────────────────────────────────────────────────────────────────

    /**
     * Creates or updates an ad. `asDraft` keeps it in `Draft`; otherwise it moves to `Submitted`
     * and `submittedOn` is stamped.
     */
    async saveAd(payload: SaveAdPayload, asDraft: boolean): Promise<MarketingAd> {
        await this.simulateLatency();

        // TODO(backend): replace with `environment.apis.saveMarketingAd`, POST `payload` with the
        // Authorization header, and return `httpResponse.data.responsedata`.

        const ads: MarketingAd[] = this.snapshot();
        const now: string = new Date().toISOString();
        const status: AdStatus = asDraft ? 'Draft' : 'Submitted';
        const existingIndex: number = payload.id
            ? ads.findIndex((ad) => ad.id === payload.id)
            : -1;

        if (existingIndex > -1) {
            const updated: MarketingAd = {
                ...ads[existingIndex],
                ...payload,
                status,
                submittedOn: asDraft ? ads[existingIndex].submittedOn : now,
            };
            ads[existingIndex] = updated;
            this.adsSubject.next([...ads]);
            return updated;
        }

        const nextId: number = ads.reduce((max, ad) => Math.max(max, ad.id), 0) + 1;
        const created: MarketingAd = {
            ...payload,
            id: nextId,
            referenceNo: this.buildReferenceNo(nextId),
            status,
            createdBy: this.sessionStorageService.getCurrentUserPersonName() || 'Current User',
            createdByRepCode: String(this.sessionStorageService.getCurrentRepCode() ?? ''),
            createdOn: now,
            submittedOn: asDraft ? null : now,
            lastReviewedOn: null,
            reviews: [],
            comments: [],
        };

        this.adsSubject.next([created, ...ads]);
        return created;
    }

    /** Appends a review to an ad and moves the ad to the reviewer's decision. */
    async saveReview(adId: number, review: Omit<AdReview, 'id'>): Promise<MarketingAd> {
        await this.simulateLatency();

        // TODO(backend): replace with `environment.apis.saveMarketingAdReview`.

        const ads: MarketingAd[] = this.snapshot();
        const index: number = ads.findIndex((ad) => ad.id === adId);

        if (index === -1) {
            throw new Error(`Advertisement ${adId} was not found.`);
        }

        const nextReviewId: number = ads[index].reviews.reduce((max, r) => Math.max(max, r.id), 0) + 1;
        const updated: MarketingAd = {
            ...ads[index],
            status: review.decision,
            lastReviewedOn: review.reviewedOn,
            reviews: [{ ...review, id: nextReviewId }, ...ads[index].reviews],
        };

        ads[index] = updated;
        this.adsSubject.next([...ads]);
        return updated;
    }



    /**
     * Adds a comment pinned to a spot on the material. Opens as `Open` and takes the next badge
     * number — numbers are never reused, so a deleted pin's number does not reappear on another.
     */
    async addPinComment(adId: number, text: string, pin: AdCommentPin): Promise<MarketingAd> {
        await this.simulateLatency();

        // TODO(backend): replace with `environment.apis.saveMarketingAdComment`, sending `pin`.

        return this.mutateAd(adId, (ad) => {
            const nextId: number = ad.comments.reduce((max, comment) => Math.max(max, comment.id), 0) + 1;
            const nextPinNo: number = ad.comments.reduce((max, comment) => Math.max(max, comment.pinNo ?? 0), 0) + 1;
            const comment: AdComment = {
                ...this.newComment(nextId, text),
                pin,
                pinNo: nextPinNo,
                status: 'Open',
                replies: [],
            };

            return { ...ad, comments: [...ad.comments, comment] };
        });
    }

    /** Adds a reply under a comment. Replies are one level deep. */
    async replyToComment(adId: number, commentId: number, text: string): Promise<MarketingAd> {
        await this.simulateLatency();

        // TODO(backend): replace with `environment.apis.saveMarketingAdComment` (with a parent id).

        return this.mutateAd(adId, (ad) => ({
            ...ad,
            comments: ad.comments.map((comment) => {
                if (comment.id !== commentId) {
                    return comment;
                }
                const replies: AdComment[] = comment.replies ?? [];
                const nextId: number = replies.reduce((max, reply) => Math.max(max, reply.id), 0) + 1;
                return { ...comment, replies: [...replies, this.newComment(nextId, text)] };
            }),
        }));
    }

    /** Resolves or reopens a pinned comment, stamping who resolved it and when. */
    async setCommentStatus(adId: number, commentId: number, status: AdCommentStatus): Promise<MarketingAd> {
        await this.simulateLatency();

        // TODO(backend): replace with a status update on `environment.apis.saveMarketingAdComment`.

        const resolved: boolean = status === 'Resolved';
        return this.mutateAd(adId, (ad) => ({
            ...ad,
            comments: ad.comments.map((comment) => (
                comment.id === commentId
                    ? {
                        ...comment,
                        status,
                        resolvedBy: resolved ? this.currentUserName() : undefined,
                        resolvedOn: resolved ? new Date().toISOString() : undefined,
                    }
                    : comment
            )),
        }));
    }

    private newComment(id: number, text: string): AdComment {
        return {
            id,
            authorName: this.currentUserName(),
            authorRole: this.sessionStorageService.getCurrentUserRole() || 'Reviewer',
            postedOn: new Date().toISOString(),
            editedOn: null,
            text: text.trim(),
        };
    }

    private currentUserName(): string {
        return this.sessionStorageService.getCurrentUserPersonName() || 'Current User';
    }

    /**
     * Swaps a returned ad's material for a new set and sends it back to the queue.
     *
     * `Returned` only — a rejection closes the submission, and the branch raises a new ad instead.
     * Replacing always resubmits: new files on an ad left `Returned` would sit where no reviewer
     * looks. The hero creative follows the new set — the old key visual belongs to the material
     * being withdrawn.
     */
    async replaceMaterialAndResubmit(adId: number, attachments: AdAttachment[]) {
        await this.simulateLatency();

        // TODO(backend): replace with `environment.apis.saveMarketingAd` (or a dedicated resubmit
        // endpoint), sending the new attachment ids with the Authorization header.

        // return this.mutateAd(adId, (ad) => {
        //     if (ad.status !== 'Returned') {
        //         throw new Error(`Advertisement ${adId} is ${ad.status}; only a returned ad can be resubmitted.`);
        //     }
        //     return {
        //         ...ad,
        //         attachments,
        //         previewImageUrl: attachments.find((attachment) => this.fileKindOf(attachment) === 'image')?.url ?? '',
        //         status: 'Submitted',
        //         submittedOn: new Date().toISOString(),
        //     };
        // });
    }

    /** Applies a change to one ad in the store and republishes it. */
    private mutateAd(adId: number, change: (ad: MarketingAd) => MarketingAd): MarketingAd {
        const ads: MarketingAd[] = this.snapshot();
        const index: number = ads.findIndex((ad) => ad.id === adId);

        if (index === -1) {
            throw new Error(`Advertisement ${adId} was not found.`);
        }

        const updated: MarketingAd = change(ads[index]);
        ads[index] = updated;
        this.adsSubject.next([...ads]);
        return updated;
    }

    /**
     * Registers a creative file against the current draft.
     *
     * Mock only — the object URL is valid for this page load. The real endpoint is the shared
     * `environment.apis.fileUpload` (`/v22/pias/fileupload`), which is multipart and returns a
     * server-hosted URL.
     */
    async uploadAdAsset(file: File): Promise<AdAttachment> {
        await this.simulateLatency();

        return {
            id: Date.now(),
            fileName: file.name,
            fileType: file.type || 'application/octet-stream',
            fileSizeKb: Math.max(1, Math.round(file.size / 1024)),
            url: URL.createObjectURL(file),
        };
    }

    /**
     * Upload ceilings. Video gets its own — a 30-second banner cut is routinely past the limit
     * that is generous for a deck or a flyer.
     */
    readonly MAX_FILE_SIZE_MB: number = 10;
    readonly MAX_VIDEO_SIZE_MB: number = 50;

    /**
     * Sorts picked or dropped files into what may be uploaded and what may not, by type first and
     * then by size. Shared by the create page and the review page's re-upload, so both enforce the
     * same rules. `accept` on the input only filters the picker; drag-and-drop bypasses it.
     */
    screenAdFiles(files: File[]): { accepted: File[]; wrongType: File[]; tooLarge: File[] } {
        const extensions: string[] = AD_ACCEPTED_FORMATS.flatMap((format) => format.extensions);
        const isAcceptedType = (file: File): boolean => (
            extensions.some((extension) => file.name.toLowerCase().endsWith(extension))
        );
        const isTooLarge = (file: File): boolean => {
            const isVideo: boolean = this.fileKindOf(file.name) === 'video';
            return file.size > (isVideo ? this.MAX_VIDEO_SIZE_MB : this.MAX_FILE_SIZE_MB) * 1024 * 1024;
        };

        const rightType: File[] = files.filter(isAcceptedType);

        return {
            wrongType: files.filter((file) => !isAcceptedType(file)),
            tooLarge: rightType.filter(isTooLarge),
            accepted: rightType.filter((file) => !isTooLarge(file)),
        };
    }

    // ── Helpers ─────────────────────────────────────────────────────────────────

    /** True when the status still needs a reviewer to act on it. */
    isPendingReview(status: AdStatus): boolean {
        return PENDING_REVIEW_STATUSES.includes(status);
    }

    /** Distinct branch names present in the store, for filter dropdowns. */
    getBranchNames(): string[] {
        return this.branchNamesOf(this.snapshot());
    }

    /** Every branch named across the given ads, deduplicated and sorted. */
    private branchNamesOf(ads: MarketingAd[]): string[] {
        return [...new Set(ads.flatMap((ad) => ad.branches))].filter(Boolean).sort();
    }

    // ── File kinds ──────────────────────────────────────────────────────────────

    /**
     * File family of an attachment, resolved from its extension first and its MIME type second.
     *
     * The extension wins because the Office MIME types are both long and easy to lose: a `.pptx`
     * dragged in from some clients arrives as `application/octet-stream`, which would otherwise
     * fall through to `other` and lose its document card.
     */
    fileKindOf(attachment: string): AdFileKind {
        const byExtension: AdFileKind | undefined = KIND_BY_EXTENSION[this.extensionOf(attachment)];
        if (byExtension) {
            return byExtension;
        }

        const fileType: string = (attachment).toLowerCase();
        if (fileType.startsWith('image/')) {
            return 'image';
        }
        if (fileType.startsWith('video/')) {
            return 'video';
        }
        if (fileType === 'application/pdf') {
            return 'pdf';
        }
        if (fileType.includes('presentation') || fileType.includes('powerpoint')) {
            return 'slides';
        }
        if (fileType.includes('spreadsheet') || fileType.includes('excel') || fileType === 'text/csv') {
            return 'spreadsheet';
        }
        if (fileType.includes('word') || fileType === 'application/msword' || fileType === 'text/plain') {
            return 'document';
        }
        return 'other';
    }

    /** Short badge for an attachment, e.g. `PNG`, `MP4`, `PPT`. */
    formatLabelOf(attachment: string): string {
        return BADGE_BY_EXTENSION[this.extensionOf(attachment)]
            ?? AD_FILE_KIND_META[this.fileKindOf(attachment)].label;
    }

    /** Material icon ligature for an attachment's kind. */
    iconOf(attachment: string): string {
        return AD_FILE_KIND_META[this.fileKindOf(attachment)].icon;
    }

    /** Palette key for an attachment's kind, used to tint its document card and badge. */
    toneOf(attachment: string): string {
        return AD_FILE_KIND_META[this.fileKindOf(attachment)].tone;
    }

    /** `1.2 MB` / `412 KB`, from the stored kilobyte size. */
    fileSizeLabel(fileSizeKb: number): string {
        return fileSizeKb >= 1024
            ? `${(fileSizeKb / 1024).toFixed(1)} MB`
            : `${fileSizeKb} KB`;
    }

    /** Lowercase extension without the dot, `''` when the name carries none. */
    private extensionOf(fileName: string): string {
        const name: string = (fileName || '').trim().toLowerCase();
        const dot: number = name.lastIndexOf('.');
        return dot > -1 ? name.slice(dot + 1) : '';
    }

    private snapshot(): MarketingAd[] {
        return [...this.adsSubject.value];
    }

    private simulateLatency(): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, this.MOCK_LATENCY_MS));
    }

    private buildReferenceNo(id: number): string {
        return `MR-${new Date().getFullYear()}-${String(id).padStart(4, '0')}`;
    }

    private applyFilter(ads: MarketingAd[], filter?: Partial<AdsListFilter>): MarketingAd[] {
        if (!filter) {
            return ads;
        }

        const search: string = (filter.search ?? '').trim().toLowerCase();

        return ads.filter((ad) => {
            if (filter.status && ad.status !== filter.status) {
                return false;
            }
            if (filter.channel && ad.channel !== filter.channel) {
                return false;
            }
            if (filter.branch && !ad.branches.includes(filter.branch)) {
                return false;
            }
            if (!search) {
                return true;
            }
            return [ad.referenceNo, ad.title, ad.category, ad.createdBy, ...ad.branches]
                .join(' ')
                .toLowerCase()
                .includes(search);
        });
    }

    // ── Mock data ───────────────────────────────────────────────────────────────

    /**
     * Seed data covering every status and a spread of branches, so all four pages have something
     * meaningful to show. Two rows are attributed to the signed-in user, which — now that the list
     * page is scoped to the signed-in user — is what keeps that page non-empty.
     *
     * Material comes from the business's sample pack (see `SAMPLES`) and covers every accepted
     * type: PNG, JPG/JPEG, PDF, MP4, PPT, Word and Excel. Some rows deliberately carry no image —
     * a Word or Excel submission has no thumbnail — so the type-coded placeholder is exercised too.
     * GIF is accepted and previews as an image, but the sample pack contains none to seed.
     */
    private buildMockAds(): MarketingAd[] {
        const currentUser: string = this.sessionStorageService.getCurrentUserPersonName() || 'Current User';
        const currentRepCode: string = String(this.sessionStorageService.getCurrentRepCode() ?? '');

        const criteriaAllYes = (remarks: string): AdReview['criteria'] =>
            AD_REVIEW_CRITERIA.map((criterion) => ({
                key: criterion.key,
                label: criterion.label,
                response: 'Yes' as const,
                remarks: criterion.key === 'disclaimers' ? remarks : '',
            }));

        // `comments` is attached below rather than repeated on every literal.
        const ads: Array<Omit<MarketingAd, 'comments'>> = [
            // {
            //     id: 11,
            //     referenceNo: 'MR-2026-0012',
            //     title: 'Cancer Decoded — Complimentary Webinar',
            //     branches: ['Orchard'],
            //     targetRecipients: 'Existing clients and prospects aged 35-55',
            //     expectedDisseminationDate: '2026-09-22',
            //     disseminationNote: '',
            //     modeOfDissemination: 'Email, WhatsApp broadcast',
            //     materialType: 'EI',
            //     channel: 'Webinar',
            //     category: 'Event / Webinar',
            //     targetAudience: 'Existing clients aged 35-55',
            //     campaignStartDate: '2026-09-22',
            //     campaignEndDate: '2026-10-15',
            //     description:
            //         'Promotional creative for the "Cancer Decoded" client education webinar, covering early detection, treatment costs and the role of critical illness cover.',
            //     bodyCopy:
            //         'Join our specialist panel for an hour on what a cancer diagnosis really costs — and how to prepare for it. Free for clients and one guest.',
            //     callToAction: 'Reserve your seat',
            //     disclaimers:
            //         'This session is for general information only and does not constitute financial advice. Buying a life insurance policy is a long-term commitment.',
            //     previewImageUrl: 'assets/images/marketing-review/webinar-cancer-decoded.png',
            //     attachments: [
            //         { id: 1, fileName: 'webinar-cancer-decoded.png', fileType: 'image/png', fileSizeKb: 462, url: 'assets/images/marketing-review/webinar-cancer-decoded.png' },
            //         { id: 2, fileName: 'mmaf-application-form-v5.pdf', fileType: 'application/pdf', fileSizeKb: 235, url: SAMPLES + 'mmaf-application-form-v5.pdf' },
            //     ],
            //     status: 'Submitted',
            //     createdBy: currentUser,
            //     createdByRepCode: currentRepCode,
            //     createdOn: '2026-09-02T09:15:00.000Z',
            //     submittedOn: '2026-09-04T02:30:00.000Z',
            //     lastReviewedOn: null,
            //     reviews: [],
            // },
            {
                id: 11,
                referenceNo: 'MR-2026-0011',
                title: 'Mid-Autumn Client Appreciation Greeting',
                branches: ['Orchard', 'Bishan'],
                targetRecipients: 'All clients',
                expectedDisseminationDate: '',
                disseminationNote: 'Immediate upon approval',
                modeOfDissemination: 'Instagram, WhatsApp broadcast',
                materialType: 'SCP',
                channel: 'Social Media',
                category: 'Festive Greeting',
                targetAudience: 'All clients',
                campaignStartDate: '2026-09-18',
                campaignEndDate: '2026-09-30',
                description: 'Festive greeting card for Instagram and WhatsApp broadcast, raised jointly by Orchard and Bishan.',
                bodyCopy: 'Wishing you and your family a warm and prosperous Mid-Autumn Festival.',
                callToAction: '',
                disclaimers: '',
                previewImageUrl: SAMPLES + 'whatsapp-broadcast.jpeg',
                attachments: [
                    { id: 3, fileName: 'mid-autumn-broadcast.jpeg', fileType: 'image/jpeg', fileSizeKb: 529, url: SAMPLES + 'whatsapp-broadcast.jpeg' },
                ],
                status: 'Draft',
                createdBy: currentUser,
                createdByRepCode: currentRepCode,
                createdOn: '2026-09-08T06:45:00.000Z',
                submittedOn: null,
                lastReviewedOn: null,
                reviews: [],
            },
            {
                id: 10,
                referenceNo: 'MR-2026-0010',
                title: 'PIAS × Tuas Power — Complimentary Insurance Coverage',
                branches: ['Tampines', 'Jurong'],
                targetRecipients: 'Existing clients, prospects',
                expectedDisseminationDate: '2026-09-15',
                disseminationNote: '',
                modeOfDissemination: 'Hardcopy, email',
                materialType: 'FLY',
                channel: 'Print',
                category: 'Product Promotion',
                targetAudience: 'Households switching electricity retailer',
                campaignStartDate: '2026-09-15',
                campaignEndDate: '2026-10-30',
                description: 'A5 leaflet for the Tuas Power tie-up offering 12 months complimentary insurance coverage.',
                bodyCopy: 'Switch your electricity plan and receive 12 months of complimentary insurance coverage, arranged through your PIAS adviser.',
                callToAction: 'Speak to your adviser',
                disclaimers: 'Coverage is underwritten by the insurer and subject to their terms. Illustrations are not guaranteed.',
                previewImageUrl: '',
                attachments: [
                    { id: 4, fileName: 'tuas-power-a5-flyer.pdf', fileType: 'application/pdf', fileSizeKb: 339, url: SAMPLES + 'tuas-power-a5-flyer.pdf' },
                ],
                status: 'In_Review',
                createdBy: 'Serene Tan',
                createdByRepCode: 'PIAS0421',
                createdOn: '2026-08-28T01:20:00.000Z',
                submittedOn: '2026-08-29T08:05:00.000Z',
                lastReviewedOn: null,
                reviews: [],
            },
            {
                id: 9,
                referenceNo: 'MR-2026-0009',
                title: 'Bankers Advertisement — Career Switcher EDM',
                channel: 'Email',
                branches: ['Jurong'],
                targetRecipients: 'Prospects in banking and financial services',
                expectedDisseminationDate: '2026-09-01',
                disseminationNote: '',
                modeOfDissemination: 'Email',
                materialType: 'EDM',
                category: 'Recruitment',
                targetAudience: 'Bankers considering a move to advisory',
                campaignStartDate: '2026-09-01',
                campaignEndDate: '2026-11-30',
                description: 'Word copy deck for the "bankers" recruitment EDM, submitted for wording review before design.',
                bodyCopy: 'You already know the products. Here is what changes when the advice is yours to own.',
                callToAction: 'Arrange a confidential chat',
                disclaimers: 'This email is intended for the named recipient only. It does not constitute a recommendation to buy or sell any product.',
                previewImageUrl: '',
                attachments: [
                    {
                        id: 7,
                        fileName: 'bankers-advertisement.docx',
                        fileType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                        fileSizeKb: 32,
                        url: SAMPLES + 'bankers-advertisement.docx',
                    },
                ],
                status: 'Approved',
                createdBy: 'Marcus Lim',
                createdByRepCode: 'PIAS0188',
                createdOn: '2026-08-12T03:00:00.000Z',
                submittedOn: '2026-08-13T05:40:00.000Z',
                lastReviewedOn: '2026-08-18T07:12:00.000Z',
                reviews: [
                    {
                        id: 1,
                        reviewerName: 'Priya Raman',
                        reviewerRole: 'Marketing Compliance',
                        reviewedOn: '2026-08-18T07:12:00.000Z',
                        decision: 'Approved',
                        overallComments:
                            'Tone is right for the segment and the claims are all soft. Approved for the stated period — please resubmit if the CTA link changes.',
                        criteria: criteriaAllYes('Footer disclaimer present and legible at 100% zoom.'),
                    },
                ],
            },
            {
                id: 8,
                referenceNo: 'MR-2026-0008',
                title: 'Adviser Recruitment Campaign — LinkedIn Carousel',
                branches: ['Jurong', 'Raffles'],
                targetRecipients: 'Prospects — career switchers, 28-40',
                expectedDisseminationDate: '',
                disseminationNote: 'Immediate upon approval',
                modeOfDissemination: 'LinkedIn sponsored posts, organisation social media page',
                materialType: 'SCP',
                channel: 'Social Media',
                category: 'Recruitment',
                targetAudience: 'Career switchers, 28-40',
                campaignStartDate: '2026-08-20',
                campaignEndDate: '2026-10-20',
                description: 'Sponsored LinkedIn carousel for the Q4 adviser intake — deck plus the organisation page key visual.',
                bodyCopy: 'Build a practice, not just a pipeline. Structured training, real mentorship, and a book you actually own.',
                callToAction: 'Apply now',
                disclaimers: '',
                previewImageUrl: SAMPLES + 'social-page-home.jpeg',
                attachments: [
                    {
                        id: 5,
                        fileName: 'branch-roadshow-deck.pptx',
                        fileType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                        fileSizeKb: 1883,
                        url: SAMPLES + 'branch-roadshow-deck.pptx',
                    },
                    { id: 8, fileName: 'social-page-home.jpeg', fileType: 'image/jpeg', fileSizeKb: 734, url: SAMPLES + 'social-page-home.jpeg' },
                ],
                status: 'Returned',
                createdBy: 'Marcus Lim',
                createdByRepCode: 'PIAS0188',
                createdOn: '2026-08-05T02:10:00.000Z',
                submittedOn: '2026-08-06T04:25:00.000Z',
                lastReviewedOn: '2026-08-11T09:50:00.000Z',
                reviews: [
                    {
                        id: 1,
                        reviewerName: 'Priya Raman',
                        reviewerRole: 'Marketing Compliance',
                        reviewedOn: '2026-08-11T09:50:00.000Z',
                        decision: 'Returned',
                        overallComments:
                            'Two things before this can go out: the income figure on slide 3 needs a source or it has to come out, and the PIAS logo is stretched on the last frame.',
                        criteria: AD_REVIEW_CRITERIA.map((criterion) => ({
                            key: criterion.key,
                            label: criterion.label,
                            response: criterion.key === 'accuracy' || criterion.key === 'branding' ? ('No' as const) : ('Yes' as const),
                            remarks:
                                criterion.key === 'accuracy'
                                    ? 'Slide 3 quotes "$120k in year two" with no basis. Remove or substantiate.'
                                    : criterion.key === 'branding'
                                        ? 'Logo aspect ratio distorted on the final frame.'
                                        : '',
                        })),
                    },
                ],
            },
            {
                id: 7,
                referenceNo: 'MR-2026-0007',
                title: 'Critical Illness Awareness Post',
                branches: ['Bishan'],
                targetRecipients: 'Social media audience',
                expectedDisseminationDate: '',
                disseminationNote: 'Immediate upon approval',
                modeOfDissemination: 'Social media posts',
                materialType: 'SCP',
                channel: 'Social Media',
                category: 'Product Promotion',
                targetAudience: 'Working adults 30-45',
                campaignStartDate: '2026-07-15',
                campaignEndDate: '2026-08-15',
                description: 'Single-image Instagram post on early-stage critical illness cover. Submitted as copy only — the creative was never attached.',
                bodyCopy: '1 in 4 Singaporeans will face a critical illness. Only some of them will have planned for it.',
                callToAction: 'DM to learn more',
                disclaimers: '',
                previewImageUrl: '',
                attachments: [],
                status: 'Rejected',
                createdBy: 'Wei Ling Ho',
                createdByRepCode: 'PIAS0733',
                createdOn: '2026-07-01T07:30:00.000Z',
                submittedOn: '2026-07-02T01:15:00.000Z',
                lastReviewedOn: '2026-07-07T06:00:00.000Z',
                reviews: [
                    {
                        id: 1,
                        reviewerName: 'Daniel Koh',
                        reviewerRole: 'Marketing Compliance',
                        reviewedOn: '2026-07-07T06:00:00.000Z',
                        decision: 'Rejected',
                        overallComments:
                            'The statistic is unsourced and the framing is fear-led without any product disclosure. This needs to be rebuilt rather than amended — happy to look at a fresh concept.',
                        criteria: AD_REVIEW_CRITERIA.map((criterion) => ({
                            key: criterion.key,
                            label: criterion.label,
                            response: criterion.key === 'disclaimers' || criterion.key === 'accuracy' ? ('No' as const) : ('Yes' as const),
                            remarks: criterion.key === 'disclaimers' ? 'No product disclosure or disclaimer anywhere in the creative.' : '',
                        })),
                    },
                ],
            },
            {
                id: 6,
                referenceNo: 'MR-2026-0006',
                title: 'iCondo App Banner — Launch Video',
                branches: ['Bishan'],
                targetRecipients: 'Prospects — condominium residents',
                expectedDisseminationDate: '2026-09-25',
                disseminationNote: '',
                modeOfDissemination: 'Social media posts, in-app banner',
                materialType: 'VID',
                channel: 'Social Media',
                category: 'Brand Awareness',
                targetAudience: 'Condominium residents',
                campaignStartDate: '2026-09-05',
                campaignEndDate: '2026-09-25',
                description: 'Animated banner running in the iCondo app and as a social cut-down.',
                bodyCopy: 'Advice you can sit down with — now a tap away in your condo app.',
                callToAction: 'Visit us',
                disclaimers: 'PIAS is a licensed financial adviser regulated by the Monetary Authority of Singapore.',
                previewImageUrl: '',
                attachments: [
                    { id: 6, fileName: 'icondo-app-banner.mp4', fileType: 'video/mp4', fileSizeKb: 542, url: SAMPLES + 'icondo-app-banner.mp4' },
                ],
                status: 'Submitted',
                createdBy: 'Wei Ling Ho',
                createdByRepCode: 'PIAS0733',
                createdOn: '2026-09-01T08:00:00.000Z',
                submittedOn: '2026-09-03T03:45:00.000Z',
                lastReviewedOn: null,
                reviews: [],
            },
            {
                id: 5,
                referenceNo: 'MR-2026-0005',
                title: 'Q4 Investment Outlook Landing Page',
                branches: ['Raffles'],
                targetRecipients: 'Existing clients — accredited investors',
                expectedDisseminationDate: '2026-10-01',
                disseminationNote: '',
                modeOfDissemination: 'Website, email',
                materialType: 'WEB',
                channel: 'Website',
                category: 'Educational Content',
                targetAudience: 'Accredited investors',
                campaignStartDate: '2026-10-01',
                campaignEndDate: '2026-12-31',
                description: 'Gated landing page hosting the Q4 outlook deck.',
                bodyCopy: 'Where our strategists see risk and opportunity heading into the final quarter.',
                callToAction: 'Download the outlook',
                disclaimers: 'Views expressed are those of the authors at the time of writing and are subject to change without notice.',
                previewImageUrl: '',
                attachments: [],
                status: 'In_Review',
                createdBy: 'Ashwin Menon',
                createdByRepCode: 'PIAS0055',
                createdOn: '2026-09-05T04:20:00.000Z',
                submittedOn: '2026-09-06T00:55:00.000Z',
                lastReviewedOn: null,
                reviews: [],
            },
            {
                id: 4,
                referenceNo: 'MR-2026-0004',
                title: 'Wealth Transfer Seminar EDM',
                branches: ['Raffles'],
                targetRecipients: 'Existing clients — business owners',
                expectedDisseminationDate: '2026-08-01',
                disseminationNote: '',
                modeOfDissemination: 'Email',
                materialType: 'EDM',
                channel: 'Email',
                category: 'Event / Webinar',
                targetAudience: 'Business owners',
                campaignStartDate: '2026-08-01',
                campaignEndDate: '2026-09-10',
                description: 'EDM inviting business-owner clients to the wealth transfer seminar.',
                bodyCopy: 'Succession is a decision, not an event. Spend an evening with our estate planning specialists.',
                callToAction: 'RSVP',
                disclaimers: 'Seats are limited and allocated on a first-come basis. This invitation is non-transferable.',
                previewImageUrl: '',
                attachments: [],
                status: 'Approved',
                createdBy: 'Ashwin Menon',
                createdByRepCode: 'PIAS0055',
                createdOn: '2026-07-18T02:40:00.000Z',
                submittedOn: '2026-07-19T06:10:00.000Z',
                lastReviewedOn: '2026-07-23T08:35:00.000Z',
                reviews: [
                    {
                        id: 1,
                        reviewerName: 'Daniel Koh',
                        reviewerRole: 'Marketing Compliance',
                        reviewedOn: '2026-07-23T08:35:00.000Z',
                        decision: 'Approved',
                        overallComments: 'Clean. No product claims, clear disclaimer, correct adviser details. Good to go.',
                        criteria: criteriaAllYes(''),
                    },
                ],
            },
            {
                id: 3,
                referenceNo: 'MR-2026-0003',
                title: 'Health Screening Partner Promotion',
                branches: ['Tampines'],
                targetRecipients: 'Existing policyholders',
                expectedDisseminationDate: '2026-06-01',
                disseminationNote: '',
                modeOfDissemination: 'Hardcopy insert',
                materialType: 'BRO',
                channel: 'Print',
                category: 'Product Promotion',
                targetAudience: 'Existing policyholders',
                campaignStartDate: '2026-06-01',
                campaignEndDate: '2026-07-31',
                description: 'Insert card promoting the discounted health screening partnership.',
                bodyCopy: 'Your policy now comes with a preferred rate on annual screening at our partner clinics.',
                callToAction: 'Show this card at the clinic',
                disclaimers: 'Partner benefits are provided by third parties and may be varied or withdrawn without notice.',
                previewImageUrl: '',
                attachments: [],
                status: 'Approved',
                createdBy: 'Serene Tan',
                createdByRepCode: 'PIAS0421',
                createdOn: '2026-05-14T05:05:00.000Z',
                submittedOn: '2026-05-15T02:20:00.000Z',
                lastReviewedOn: '2026-05-20T04:15:00.000Z',
                reviews: [
                    {
                        id: 1,
                        reviewerName: 'Priya Raman',
                        reviewerRole: 'Marketing Compliance',
                        reviewedOn: '2026-05-20T04:15:00.000Z',
                        decision: 'Approved',
                        overallComments: 'Third-party benefit wording matches the partner agreement. Approved.',
                        criteria: criteriaAllYes('Partner disclaimer added on the reverse as requested.'),
                    },
                ],
            },
            {
                id: 2,
                referenceNo: 'MR-2026-0002',
                title: 'Marketing Materials Approval Code Register',
                branches: ['Orchard', 'Tampines'],
                targetRecipients: 'Internal — branch marketing coordinators',
                expectedDisseminationDate: '',
                disseminationNote: 'Immediate upon approval',
                modeOfDissemination: 'Internal circulation',
                materialType: 'OTH',
                channel: 'Collateral',
                category: 'Brand Awareness',
                targetAudience: 'Internal — advisers',
                campaignStartDate: '2026-04-01',
                campaignEndDate: '2026-12-31',
                description: 'Working register of issued approval codes, circulated to branch coordinators each quarter.',
                bodyCopy: 'Track every approval code issued, the material it covers and its expiry.',
                callToAction: '',
                disclaimers: 'Any amendment to an approved material voids its approval code and requires resubmission.',
                previewImageUrl: '',
                attachments: [
                    {
                        id: 9,
                        fileName: 'approval-code-register.xlsx',
                        fileType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        fileSizeKb: 11,
                        url: SAMPLES + 'approval-code-register.xlsx',
                    },
                ],
                status: 'Draft',
                createdBy: 'Serene Tan',
                createdByRepCode: 'PIAS0421',
                createdOn: '2026-03-22T09:00:00.000Z',
                submittedOn: null,
                lastReviewedOn: null,
                reviews: [],
            },
            {
                id: 1,
                referenceNo: 'MR-2026-0001',
                title: 'New Year Financial Health Check',
                branches: ['Jurong'],
                targetRecipients: 'All clients, social media audience',
                expectedDisseminationDate: '2026-01-05',
                disseminationNote: '',
                modeOfDissemination: 'Social media posts',
                materialType: 'SCP',
                channel: 'Social Media',
                category: 'Educational Content',
                targetAudience: 'All clients',
                campaignStartDate: '2026-01-05',
                campaignEndDate: '2026-02-28',
                description: 'Single-image post prompting clients to book an annual review.',
                bodyCopy: 'Five questions worth asking about your money before the year gets away from you.',
                callToAction: 'Book your review',
                disclaimers: 'General information only. Please seek advice specific to your circumstances.',
                previewImageUrl: SAMPLES + 'congratulations-post.jpg',
                attachments: [
                    { id: 10, fileName: 'new-year-health-check.jpg', fileType: 'image/jpeg', fileSizeKb: 288, url: SAMPLES + 'congratulations-post.jpg' },
                ],
                status: 'Approved',
                createdBy: 'Wei Ling Ho',
                createdByRepCode: 'PIAS0733',
                createdOn: '2025-12-10T01:30:00.000Z',
                submittedOn: '2025-12-11T03:00:00.000Z',
                lastReviewedOn: '2025-12-16T05:45:00.000Z',
                reviews: [
                    {
                        id: 1,
                        reviewerName: 'Daniel Koh',
                        reviewerRole: 'Marketing Compliance',
                        reviewedOn: '2025-12-16T05:45:00.000Z',
                        decision: 'Approved',
                        overallComments: 'Educational, no claims, disclaimer present. Approved.',
                        criteria: criteriaAllYes(''),
                    },
                ],
            },
        ];

        /*
         * Two threads are seeded so the review page opens on a conversation rather than an empty
         * panel — one where the marketing team has asked a question, one where the branch has
         * replied. Everything else starts clean.
         */
        const seededComments: Record<number, AdComment[]> = {
            10: [
                {
                    id: 1,
                    authorName: 'Priya Raman',
                    authorRole: 'Marketing Compliance',
                    postedOn: '2026-08-30T02:10:00.000Z',
                    editedOn: null,
                    text: 'Is the 12-month coverage underwritten by Tuas Power or by the insurer? The leaflet reads as though PIAS provides it.',
                },
                {
                    id: 2,
                    authorName: 'Serene Tan',
                    authorRole: 'Financial Adviser Representative',
                    postedOn: '2026-08-30T07:45:00.000Z',
                    editedOn: null,
                    text: 'By the insurer — happy to add a line under the offer panel making that explicit before this goes to print.',
                },
                // A pinned comment, so the review page demonstrates a numbered pin on the flyer.
                {
                    id: 3,
                    authorName: 'Priya Raman',
                    authorRole: 'Marketing Compliance',
                    postedOn: '2026-08-30T08:20:00.000Z',
                    editedOn: null,
                    text: 'The insurer disclaimer needs to sit in this panel, not only in the small print on the back.',
                    pin: {
                        documentId: 4,
                        page: 1,
                        pageLabel: 'Page 1',
                        kind: 'area',
                        rects: [{ x: 0.08, y: 0.55, w: 0.84, h: 0.16 }],
                    },
                    pinNo: 1,
                    status: 'Open',
                    replies: [],
                },
            ],
            6: [
                {
                    id: 1,
                    authorName: 'Daniel Koh',
                    authorRole: 'Marketing Compliance',
                    postedOn: '2026-09-08T01:30:00.000Z',
                    editedOn: null,
                    text: 'Video looks fine. Confirm the in-app placement is only served to residents and not the public feed.',
                },
            ],
        };

        return ads.map((ad) => ({ ...ad, comments: seededComments[ad.id] ?? [] }));
    }

    //GET Marketing Application List

    async getAllMarketingReview(repcode: any) {

        const API_URL: string = environment.baseUrl + environment.apis.getAllMarketingReview;

        const REQUEST_CONFIG: AxiosRequestConfig = {
            method: 'POST',
            maxBodyLength: Infinity,
            url: API_URL,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
            },
            data: {
                "piasRepCode": repcode
            }
        };

        return await axios.request(REQUEST_CONFIG)
            .then((httpResponse) => {
                return JSON.stringify(httpResponse);
            })
            .catch((error) => {
                throw new AxiosError(error.response.data, error.status);
            });
    }

    async deleteMarketingReviewApplication(data: any) {
        const API_URL: string =
            environment.baseUrl + environment.apis.deletedraft;

        const REQUEST_CONFIG: AxiosRequestConfig = {
            method: 'DELETE',
            maxBodyLength: Infinity,
            url: API_URL,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
            },
            data: {
                applicationId: data.applicationId,
                piasRepCode: data.piasRepCode
            }
        };

        return await axios.request(REQUEST_CONFIG)
            .then((httpResponse) => JSON.stringify(httpResponse))
            .catch((error) => {
                throw new AxiosError(
                    error.response?.data,
                    error.response?.status
                );
            });
    }

    async gethistory(marketingReviewApplicationId: number) {
        const API_URL: string = environment.baseUrl + environment.apis.history.
            replace('{applicationId}', String(marketingReviewApplicationId));
        const REQUEST_CONFIG: AxiosRequestConfig = {
            method: 'GET',
            maxBodyLength: Infinity,
            url: API_URL,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': this.sessionStorageService.getCurrentUserAuthToken()
            },
        };

        return await axios.request(REQUEST_CONFIG)
            .then((httpResponse) => {
                return httpResponse.data;
            })
            .catch((error) => {
                throw new AxiosError(error.response?.data, error.status);
            });
    }

}
